import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Edit, Calendar, MapPin, Gauge, TrendingDown, DollarSign, Paperclip, Users, ClipboardCheck, AlertTriangle, Handshake, ListChecks } from 'lucide-react';
import AssetAssignments from './AssetAssignments';
import AssetMileage from './AssetMileage';
import AssetDepreciation from './AssetDepreciation';
import AssetExpenses from './AssetExpenses';
import AssetFiles from './AssetFiles';
import AssetInspections from './AssetInspections';
import AssetDamageReports from './AssetDamageReports';
import AssetTCO from './AssetTCO';
import AssetHandovers from './AssetHandovers';
import AssetChecklists from './AssetChecklists';
import { ownershipLabel, ownershipShort, ownershipTone, isBorrowed } from './ownership';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InfoRow = ({ label, value }) => value ? (
  <div className="flex flex-col gap-0.5 border-b border-dashed py-1.5 last:border-0 sm:flex-row sm:justify-between sm:gap-3">
    <span className="text-xs text-muted-foreground sm:text-sm">{label}</span>
    <span className="break-words text-sm font-medium sm:max-w-[60%] sm:text-right">{value}</span>
  </div>
) : null;

const AssetDetail = ({ asset: initialAsset, open, onClose, onEdit, onDeleted, onUpdated, categories, conditions, suppliers, employees, projects, isAdmin, config = {}, navigateTo }) => {
  const { t } = useTranslation();
  const [asset, setAsset] = useState(initialAsset);
  const [tab, setTab] = useState('overview');

  const refreshAsset = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/inventory/assets/${asset.id}`);
      setAsset(r.data);
      onUpdated?.(r.data);
    } catch {}
  }, [asset.id, onUpdated]);

  const isVehicle = asset.category_name === 'Vehicles';
  const borrowed = isBorrowed(asset.ownership);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="asset-detail-dialog">
        <DialogHeader>
          <div className="flex flex-col gap-2 pr-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <DialogTitle className="flex flex-wrap items-center gap-x-2 gap-y-0.5 break-words">
                <span className="font-mono text-base">{asset.inventory_number}</span>
                <span>{asset.name}</span>
              </DialogTitle>
              <DialogDescription className="sr-only">{t('inv.detail.srTitle', { name: asset.name })}</DialogDescription>
              <div className="mt-1 flex flex-wrap gap-1.5">
                <Badge variant="outline">{t(`inv.assets.type.${asset.asset_type}`, { defaultValue: asset.asset_type })}</Badge>
                {asset.category_name && <Badge variant="secondary">{asset.category_name}</Badge>}
                <Badge variant="outline" className={ownershipTone(asset.ownership)} data-testid="asset-ownership-badge">
                  {ownershipShort(asset.ownership, t)}
                </Badge>
                <Badge variant={asset.condition === 'New' ? 'default' : asset.condition === 'Scrap' ? 'destructive' : 'secondary'}>{asset.condition}</Badge>
              </div>
            </div>
            {isAdmin && (
              <Button variant="outline" size="sm" onClick={() => onEdit(asset)} data-testid="edit-asset-btn">
                <Edit className="w-4 h-4 mr-1" /> {t('common.edit')}
              </Button>
            )}
          </div>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="min-w-0">
          <TabsList className="h-auto w-full flex-wrap justify-start gap-1">
            <TabsTrigger value="overview" className="text-xs" data-testid="tab-overview"><Calendar className="w-3 h-3 mr-1" />{t('inv.detail.tabs.overview')}</TabsTrigger>
            <TabsTrigger value="inspections" className="text-xs" data-testid="tab-inspections"><ClipboardCheck className="w-3 h-3 mr-1" />{t('inv.detail.tabs.inspections')}</TabsTrigger>
            <TabsTrigger value="checks" className="text-xs" data-testid="tab-checks"><ListChecks className="w-3 h-3 mr-1" />{t('inv.detail.tabs.checks')}</TabsTrigger>
            <TabsTrigger value="handover" className="text-xs" data-testid="tab-handover"><Handshake className="w-3 h-3 mr-1" />{t('inv.detail.tabs.handover')}</TabsTrigger>
            <TabsTrigger value="damage" className="text-xs" data-testid="tab-damage"><AlertTriangle className="w-3 h-3 mr-1" />{t('inv.detail.tabs.damage')}</TabsTrigger>
            <TabsTrigger value="assignments" className="text-xs" data-testid="tab-assignments"><Users className="w-3 h-3 mr-1" />{t('inv.detail.tabs.assign')}</TabsTrigger>
            {isVehicle && <TabsTrigger value="mileage" className="text-xs" data-testid="tab-mileage"><Gauge className="w-3 h-3 mr-1" />{t('inv.detail.tabs.mileage')}</TabsTrigger>}
            <TabsTrigger value="depreciation" className="text-xs" data-testid="tab-depreciation"><TrendingDown className="w-3 h-3 mr-1" />{t('inv.detail.tabs.depreciation')}</TabsTrigger>
            <TabsTrigger value="expenses" className="text-xs" data-testid="tab-expenses"><DollarSign className="w-3 h-3 mr-1" />{t('inv.detail.tabs.expenses')}</TabsTrigger>
            <TabsTrigger value="files" className="text-xs" data-testid="tab-files"><Paperclip className="w-3 h-3 mr-1" />{t('inv.detail.tabs.files')}</TabsTrigger>
          </TabsList>

          <div className="mt-2 max-h-[55vh] min-w-0 overflow-y-auto overflow-x-auto">
            <TabsContent value="overview" className="mt-0 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1 rounded-lg border p-3">
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">{t('inv.detail.general')}</h4>
                  <InfoRow label={t('inv.assets.manufacturer')} value={asset.manufacturer} />
                  <InfoRow label={t('inv.assets.model')} value={asset.model_name} />
                  <InfoRow label={t('inv.assets.partNumber')} value={asset.part_number} />
                  <InfoRow label={t('inv.assets.serialNumber')} value={asset.serial_number} />
                  <InfoRow label={t('inv.assets.manufactureDate')} value={asset.manufacture_date} />
                  <InfoRow label={t('inv.assets.registered')} value={asset.registered_date} />
                  {asset.manual_link && <InfoRow label={t('inv.files.types.manual')} value={<a href={asset.manual_link} target="_blank" rel="noreferrer" className="text-blue-600 underline">{t('inv.suppliers.link')}</a>} />}
                  {asset.description && <InfoRow label={t('inv.common.description')} value={asset.description} />}
                </div>

                <div className="space-y-1 rounded-lg border p-3">
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">{t('inv.detail.purchaseWarranty')}</h4>
                  <InfoRow label={t('inv.assets.supplier')} value={asset.supplier_name} />
                  <InfoRow label={t('inv.assets.purchaseDate')} value={asset.purchase_date} />
                  <InfoRow label={t('inv.common.value')} value={asset.purchase_value ? `${asset.purchase_value.toLocaleString()} ${asset.purchase_currency}` : null} />
                  <InfoRow label={t('inv.tco.vat')} value={asset.vat_value ? `${asset.vat_value.toLocaleString()} ${asset.purchase_currency}` : null} />
                  <InfoRow label={t('inv.assets.warrantyUntil')} value={asset.warranty_date} />
                  {asset.shop_link && <InfoRow label={t('inv.assets.shop')} value={<a href={asset.shop_link} target="_blank" rel="noreferrer" className="text-blue-600 underline">{t('inv.suppliers.link')}</a>} />}
                </div>

                <div className="space-y-1 rounded-lg border p-3" data-testid="ownership-card">
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">{t('inv.detail.ownership')}</h4>
                  <InfoRow label={t('inv.detail.heldAs')} value={ownershipLabel(asset.ownership, t)} />
                  <InfoRow label={t('inv.detail.owner')} value={asset.owner_customer_name} />
                  <InfoRow label={t('inv.detail.providedFor')} value={asset.provided_for_project_name} />
                  <InfoRow label={t('inv.hire.hireCompany')} value={asset.rental?.supplier_name} />
                  <InfoRow label={t('inv.detail.hireRef')} value={asset.rental?.order_reference} />
                  <InfoRow label={t('inv.detail.hireRate')} value={asset.rental?.rate
                    ? `${asset.rental.rate} ${asset.rental.currency || ''} / ${t(`inv.ownership.periodShort.${asset.rental.rate_period || 'day'}`)}` : null} />
                  <InfoRow label={t('inv.detail.hirePeriod')} value={[asset.rental?.start_date, asset.rental?.end_date].filter(Boolean).join(' → ') || null} />
                  <InfoRow label={t('inv.detail.offHired')} value={asset.rental?.off_hire_date} />
                  <InfoRow label={t('inv.detail.conditionOnReceipt')} value={asset.condition_on_receipt} />
                  <InfoRow label={t('inv.detail.returnExpected')} value={asset.expected_return_date} />
                  <InfoRow label={t('inv.detail.returned')} value={asset.returned_date} />
                  {borrowed && !asset.returned_date && asset.expected_return_date
                    && asset.expected_return_date < new Date().toISOString().slice(0, 10) && (
                    <p className="mt-2 rounded bg-red-50 p-2 text-xs text-red-700" data-testid="return-overdue-note">
                      {t('inv.detail.pastReturnNote')}
                    </p>
                  )}
                </div>

                <div className="space-y-1 rounded-lg border p-3">
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">{t('inv.detail.conditionCompliance')}</h4>
                  <InfoRow label={t('inv.common.condition')} value={asset.condition} />
                  <InfoRow label={t('inv.detail.lastInspection')} value={asset.inspection_date} />
                  <InfoRow label={t('inv.detail.nextInspection')} value={asset.next_inspection_date} />
                  <InfoRow label={t('inv.detail.lastPat')} value={asset.pat_test_date} />
                  <InfoRow label={t('inv.detail.nextPat')} value={asset.next_pat_test_date} />
                  <InfoRow label={t('inv.detail.replaceBy')} value={asset.replace_by_date} />
                </div>

                {isVehicle && asset.vehicle_info && (
                  <div className="space-y-1 rounded-lg border p-3">
                    <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">{t('inv.detail.vehicle')}</h4>
                    <InfoRow label={t('inv.detail.regPlate')} value={asset.vehicle_info.registration_plate} />
                    <InfoRow label={t('auto.inv.vin')} value={asset.vehicle_info.vin_number} />
                    <InfoRow label={t('inv.detail.motDate')} value={asset.vehicle_info.mot_date} />
                    <InfoRow label={t('inv.detail.nextMot')} value={asset.vehicle_info.next_mot_date} />
                  </div>
                )}

                {asset.insurance && (
                  <div className="space-y-1 rounded-lg border p-3">
                    <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">{t('inv.detail.insurance')}</h4>
                    <InfoRow label={t('inv.detail.start')} value={asset.insurance.insurance_date} />
                    <InfoRow label={t('inv.detail.validUntil')} value={asset.insurance.insurance_valid_date} />
                    <InfoRow label={t('inv.detail.contact')} value={asset.insurance.contact_name} />
                    <InfoRow label={t('common.email')} value={asset.insurance.contact_email} />
                    <InfoRow label={t('common.phone')} value={asset.insurance.contact_phone} />
                  </div>
                )}

                <div className="space-y-1 rounded-lg border p-3">
                  <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">{t('inv.detail.financial')}</h4>
                  <InfoRow label={t('inv.detail.depreciationMethod')} value={asset.depreciation_method === 'straight_line' ? t('inv.depreciation.method.straightLine') : asset.depreciation_method === 'reducing_balance' ? t('inv.depreciation.method.reducingBalance') : t('common.none')} />
                  <InfoRow label={t('inv.detail.rate')} value={asset.depreciation_rate ? `${asset.depreciation_rate}%` : null} />
                  <InfoRow label={t('inv.assets.usefulLife')} value={asset.useful_life_years ? t('inv.detail.years', { count: asset.useful_life_years }) : null} />
                  <InfoRow label={t('inv.detail.residualValue')} value={asset.residual_value ? `${asset.residual_value.toLocaleString()} ${asset.purchase_currency}` : null} />
                </div>
              </div>

              {/* TCO Summary */}
              {asset.purchase_value > 0 && <AssetTCO asset={asset} />}
            </TabsContent>

            <TabsContent value="inspections" className="mt-0">
              <AssetInspections asset={asset} isAdmin={isAdmin} onRefresh={refreshAsset} config={config} employees={employees} navigateTo={navigateTo} onClose={onClose} />
            </TabsContent>

            <TabsContent value="checks" className="mt-0">
              <AssetChecklists asset={asset} navigateTo={navigateTo} onClose={onClose} />
            </TabsContent>

            <TabsContent value="handover" className="mt-0">
              <AssetHandovers asset={asset} isAdmin={isAdmin} conditions={conditions}
                customers={config.customers || []} projects={projects || []}
                employees={employees || []} onRefresh={refreshAsset} />
            </TabsContent>

            <TabsContent value="damage" className="mt-0">
              <AssetDamageReports asset={asset} isAdmin={isAdmin} onRefresh={refreshAsset} config={config} employees={employees} navigateTo={navigateTo} onClose={onClose} />
            </TabsContent>

            <TabsContent value="assignments" className="mt-0">
              <AssetAssignments asset={asset} employees={employees} projects={projects} isAdmin={isAdmin} onRefresh={refreshAsset} navigateTo={navigateTo} onClose={onClose} />
            </TabsContent>

            {isVehicle && (
              <TabsContent value="mileage" className="mt-0">
                <AssetMileage asset={asset} isAdmin={isAdmin} onRefresh={refreshAsset} />
              </TabsContent>
            )}

            <TabsContent value="depreciation" className="mt-0">
              <AssetDepreciation asset={asset} isAdmin={isAdmin} onRefresh={refreshAsset} />
            </TabsContent>

            <TabsContent value="expenses" className="mt-0">
              <AssetExpenses asset={asset} isAdmin={isAdmin} onRefresh={refreshAsset} navigateTo={navigateTo} onClose={onClose} />
            </TabsContent>

            <TabsContent value="files" className="mt-0">
              <AssetFiles asset={asset} isAdmin={isAdmin} onRefresh={refreshAsset} />
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default AssetDetail;
